export const PROPERTY_TYPE_OPTIONS = [
    { key: 'SFR',    label: 'Single Family' },
    { key: 'MFR',    label: 'Multi-Family'  },
    { key: 'TWNH',   label: 'Townhouse'     },
    { key: 'CONDO',  label: 'Condo'         },
    { key: 'MOBILE', label: 'Mobile'        },
    { key: 'LAND',   label: 'Land'          },
    { key: 'OTHER',  label: 'Other'         },
];

const PROPERTY_TYPE_LABEL_MAP = Object.fromEntries(
    PROPERTY_TYPE_OPTIONS.map(({ key, label }) => [key, label])
);

export const normalizePropertyTypeKey = (raw) => {
    if (raw === null || raw === undefined) return 'OTHER';
    const s = String(raw).trim().toLowerCase().replace(/%20/g, ' ').replace(/[_-]/g, ' ');
    if (!s) return 'OTHER';
    if (s === 'sfr' || s === 'sfh' || s.includes('single family') || s === 'residential') return 'SFR';
    if (s === 'mfr' || s.includes('multi family') || s.includes('multifamily') || s.includes('duplex') || s.includes('triplex') || s.includes('fourplex') || s.includes('quadplex')) return 'MFR';
    if (s === 'twnh' || s.includes('townhouse') || s.includes('townhome') || s.includes('row house')) return 'TWNH';
    if (s.includes('condo') || s.includes('coop') || s.includes('co op')) return 'CONDO';
    if (s.includes('mobile') || s.includes('manufactured') || s.includes('trailer')) return 'MOBILE';
    if (s.includes('land') || s.includes('lot') || s.includes('vacant')) return 'LAND';
    return 'OTHER';
};

/**
 * Does the listing text itself say this house is a teardown, is sold for lot/land value, or is already cleared
 * land? Read off `lead.teardownClaim`, which ingestion sets with an LLM (functions/utils/listingTeardownClaimLLM.js).
 * It sits BESIDE the property type and never changes it, so this is a separate question from
 * `normalizePropertyTypeKey(...) === 'LAND'`. Only an explicit `isTeardown === true` counts: a lead never read has
 * no claim, and a listing that merely pitches the land (`softBuildSite`) is not stated as a teardown.
 */
export const isTeardownLead = (lead) => lead?.teardownClaim?.isTeardown === true;

/**
 * Show the lot (size, frontage x depth) instead of the house stats (bd/ba/sqft/year built)? True for real land,
 * and for a house the listing itself calls a teardown — a bedroom count describes a building the buyer is not buying.
 *
 * `hasLotData` (default true) is whether there is any lot size or dimensions to show. Real land always swaps, as
 * before (it has no house stats worth keeping). A flagged TEARDOWN swaps only when there is a lot to show instead:
 * with none, the header would drop the house stats and render an empty row, which is worse than the house stats.
 */
export const showsLotInsteadOfHouse = (lead, { hasLotData = true } = {}) => (
    normalizePropertyTypeKey(lead?.propertyType) === 'LAND' || (isTeardownLead(lead) && hasLotData)
);

/**
 * The lot as the lead header reads it, and whether it replaces the house stats — one pure function so the whole
 * decision, including the no-lot-data fallback, is testable (no test mounts the 6,000-line page).
 *
 * `characteristics` is the lead's verifiedCharacteristics. Lot size stays raw (the page formats it in sq ft).
 * Frontage x depth are both-or-neither: half a pair ("105 x ?") is not a dimension, so a lone width or depth
 * reads as no dimensions at all. Zero, negative and non-numeric values count as absent.
 */
export const resolveHeaderLot = (lead, characteristics) => {
    const c = characteristics || {};
    const squareFeet = Number(c.lotSquareFeet) > 0 ? c.lotSquareFeet : null;
    const width = Number(c.lotWidthFeet) > 0 ? Number(c.lotWidthFeet) : null;
    const depth = Number(c.lotDepthFeet) > 0 ? Number(c.lotDepthFeet) : null;
    const hasDimensions = width != null && depth != null;
    return {
        squareFeet,
        widthFeet: hasDimensions ? width : null,
        depthFeet: hasDimensions ? depth : null,
        hasDimensions,
        showsLot: showsLotInsteadOfHouse(lead, { hasLotData: squareFeet != null || hasDimensions }),
    };
};

export const getPropertyTypeLabel = (raw) => {
    const key = normalizePropertyTypeKey(raw);
    return PROPERTY_TYPE_LABEL_MAP[key] ?? null;
};
